import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ServerFrame } from "@astrum/shared";
import { useAuth } from "./auth.js";
import { realtime, type RealtimeStatus } from "./realtime.js";

/**
 * Binds the realtime client to the React tree.
 *
 * The connection is opened when a viewer exists and closed when they sign
 * out — never on component unmount, so navigating between routes does not
 * churn the socket.
 */

type RealtimeContextValue = {
  status: RealtimeStatus;
  /** Workspace-wide online user ids. */
  onlineUserIds: Set<string>;
  /**
   * Status changes that arrived live, by user. Overrides whatever a fetched
   * payload (people list, message author) said at load time; absent means
   * "nothing newer than what you fetched".
   */
  statusOverrides: Map<string, LiveStatus>;
  subscribe: (listener: (frame: ServerFrame) => void) => () => void;
  setFocus: (channelId: string | null) => void;
  notifyTyping: (channelId: string) => void;
  onReconnect: (listener: () => void) => () => void;
};

export type LiveStatus = { emoji: string | null; text: string | null; dnd: boolean };

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { viewer } = useAuth();
  const [status, setStatus] = useState<RealtimeStatus>(realtime.getStatus());
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const [statusOverrides, setStatusOverrides] = useState<Map<string, LiveStatus>>(new Map());

  useEffect(() => {
    if (!viewer) {
      realtime.disconnect();
      setOnlineUserIds(new Set());
      setStatusOverrides(new Map());
      return;
    }

    realtime.connect();

    const offStatus = realtime.onStatus(setStatus);

    // Presence is held here rather than in each consumer: it is one small set
    // that many components read, and duplicating the reducer per subscriber
    // would mean recomputing it on every frame.
    const offFrame = realtime.on((frame) => {
      if (frame.t === "presence_sync") {
        setOnlineUserIds(new Set(frame.online));
      } else if (frame.t === "status") {
        setStatusOverrides((prev) => {
          const next = new Map(prev);
          next.set(frame.userId, { emoji: frame.emoji, text: frame.text, dnd: frame.dnd });
          return next;
        });
      } else if (frame.t === "presence") {
        setOnlineUserIds((prev) => {
          const next = new Set(prev);
          if (frame.status === "online") next.add(frame.userId);
          else next.delete(frame.userId);
          return next;
        });
      }
    });

    return () => {
      offStatus();
      offFrame();
    };
  }, [viewer]);

  // Closes the socket cleanly when the tab goes away, so presence does not
  // wait for a heartbeat to time out.
  useEffect(() => {
    const handleUnload = () => realtime.disconnect();
    window.addEventListener("pagehide", handleUnload);
    return () => window.removeEventListener("pagehide", handleUnload);
  }, []);

  const value = useMemo<RealtimeContextValue>(
    () => ({
      status,
      onlineUserIds,
      statusOverrides,
      subscribe: (listener) => realtime.on(listener),
      setFocus: (channelId) => realtime.setFocus(channelId),
      notifyTyping: (channelId) => realtime.notifyTyping(channelId),
      onReconnect: (listener) => realtime.onReconnect(listener),
    }),
    [status, onlineUserIds, statusOverrides],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error("useRealtime must be used inside a RealtimeProvider");
  return context;
}

/**
 * Subscribes to realtime frames with an always-current handler.
 *
 * The handler is held in a ref so a caller can pass an inline closure without
 * re-subscribing on every render — otherwise every state update in the
 * consuming component would tear down and rebuild the subscription.
 */
export function useRealtimeFrames(handler: (frame: ServerFrame) => void): void {
  const { subscribe } = useRealtime();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => subscribe((frame) => handlerRef.current(frame)), [subscribe]);
}

/**
 * Runs `handler` each time the socket comes back after a drop (not on the
 * first connect).
 *
 * Connect is online-only: no frame missed while disconnected is replayed,
 * so a screen showing live data refetches it here — the open thread, unread
 * counts, the notification badge.
 */
export function useReconnected(handler: () => void): void {
  const { onReconnect } = useRealtime();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => onReconnect(() => handlerRef.current()), [onReconnect]);
}

/**
 * What to show next to someone's name: the live override if a status frame
 * arrived since the page loaded, otherwise what the fetched payload said.
 */
export function useLiveStatus(userId: string | undefined, fetched: LiveStatus | null | undefined): LiveStatus | null {
  const { statusOverrides } = useRealtime();
  const live = userId ? statusOverrides.get(userId) : undefined;
  const resolved = live ?? fetched ?? null;
  return resolved && (resolved.emoji || resolved.text || resolved.dnd) ? resolved : null;
}
